import { useTheme } from "@emotion/react";
import { DeleteOutline, EditOutlined } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteLevel,
  fetchLevels,
  reassignAssessments,
} from "reduxStore/actions/levelActions";
import { setSnackbar } from "reduxStore/state";
import { checkRoles, getPermissionFlags, getUserScope } from "utils/permissions";

export const useLevelGrid = ({ handleEditOpen }) => {
  const { palette } = useTheme();

  const user = useSelector((state) => state.user);

  const { locationIds } = getUserScope(user);
  const userLocationId = locationIds[0] || "";

  const { showOrg } = getPermissionFlags(user);
  const { isAcademyImprovementLead } = checkRoles(user);

  const backgroundAlt = palette.background.alt;
  const primaryLight = palette.primary.light;
  const primaryDark = palette.primary.dark;

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("");

  const [searchInput, setSearchInput] = useState("");

  const dispatch = useDispatch();

  const levels = useSelector((state) => state.levels.data || []);
  const totalLevels = useSelector((state) => state.levels.total || 0);

  const [deleteLevelDialogOpen, setDeleteLevelDialogOpen] = useState(false);
  const [levelToDelete, setLevelToDelete] = useState(null);

  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [levelToReassign, setLevelToReassign] = useState(null);

  const handleDeleteOpen = (level) => {
    setLevelToDelete(level);
    setDeleteLevelDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setLevelToDelete(null);
    setDeleteLevelDialogOpen(false);
  };

  const handleDeleteLevelConfirm = async () => {
    try {
      const response = await dispatch(deleteLevel(levelToDelete._id));

      if (response.reassignmentRequired) {
        setLevelToReassign(levelToDelete._id);
        setReassignDialogOpen(true);
        return;
      }

      const isSuccess = response.success && response.message;
      dispatch(
        setSnackbar({
          open: true,
          message: response.message,
          severity: isSuccess ? "success" : "error",
        })
      );

      if (isSuccess) {
        closeDeleteDialog();
        setLevelToDelete(null);
        setDeleteLevelDialogOpen(false);
      }
    } catch (error) {
      console.error("Error while deleting Level:", error);
      dispatch(
        setSnackbar({
          open: true,
          message: "Failed to delete Level",
          severity: "error",
        })
      );
    }
  };

  // Handle reassignment confirmation
  const handleReassignConfirm = async (newLevelId) => {
    const response = await dispatch(
      reassignAssessments(levelToReassign, newLevelId)
    );

    if (response.success) {
      dispatch(
        setSnackbar({
          open: true,
          message: response.message,
          severity: "success",
        })
      );
      setReassignDialogOpen(false);

      // Retry deleting the level
      await handleDeleteLevelConfirm();
    } else {
      dispatch(
        setSnackbar({
          open: true,
          message: response.message,
          severity: "error",
        })
      );
    }
  };

  useEffect(() => {
    const filters = {
      page: page, // Ensure this aligns with API expectations (0-based or 1-based)
      limit: pageSize, // Use 'limit' instead of 'pageSize'
      sort,
      search,
      location: !showOrg && !isAcademyImprovementLead ? userLocationId : "",
    };
    dispatch(fetchLevels(filters));
  }, [
    page,
    pageSize,
    sort,
    search,
    dispatch,
    userLocationId,
    showOrg,
    isAcademyImprovementLead,
  ]);

  const columns = [
    { field: "name", headerName: "Level Name", flex: 2 },
    (showOrg || isAcademyImprovementLead) && {
      field: "locationCount",
      headerName: "Locations Count",
      flex: 0.5,
      renderCell: (params) => {
        const locations = params.row.locations
          .map((location) => location.name)
          .join(", ");
        const locationCount = params.row.locations.length;

        return (
          <Tooltip title={locations} arrow>
            <span>{locationCount}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 1,
      valueGetter: (params) => {
        return moment(params.row.createdAt).format("ll");
      },
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1,
      valueGetter: (params) => {
        return moment(params.row.updatedAt).format("ll");
      },
    },
    showOrg && {
      field: "edit",
      headerName: "Edit",
      flex: 0.5,
      renderCell: (params) => (
        <EditOutlined
          sx={{ color: primaryDark, cursor: "pointer" }}
          onClick={() => handleEditOpen(params.row)}
        />
      ),
    },
    showOrg && {
      field: "delete",
      headerName: "Delete",
      flex: 0.5,
      renderCell: (params) => (
        <button
          onClick={() => handleDeleteOpen(params.row)} // Open the delete dialog with unit details
          style={{ backgroundColor: "transparent", border: "none" }}
        >
          <DeleteOutline sx={{ color: "red" }} />
        </button>
      ),
    },
  ].filter(Boolean);

  // When the user stops typing for 500ms, actually apply the search:
  useEffect(() => {
    const h = setTimeout(() => {
      setPage(0);
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(h);
  }, [searchInput, setSearch, setPage]);

  return {
    backgroundAlt,
    primaryLight,
    page,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    sort,
    setSort,
    searchInput,
    setSearchInput,
    levels,
    totalLevels,
    deleteLevelDialogOpen,
    reassignDialogOpen,
    setReassignDialogOpen,
    handleDeleteLevelConfirm,
    closeDeleteDialog,
    handleReassignConfirm,
    columns,
  };
};
